// ═══════════════════════════════════════════════════════════════════
// lyraeiModel3D.jsx — LYRAEI, SCULPTED   (caP'n, 2026-09-16, session 3)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "start the work for Lyraei's proper 3D model like V'ael" …
// "let's replace it all for this new Lyraei, except when a 2D version is
// selected (since I already have the pictures lets just keep them there)."
// "Terror you can only be Lyraei" — so this body is BOTH the Lyraei folk
// id AND the female player (player_f) in the 3D Plaza. 2D screens keep
// their pictures; nothing here touches them.
// The floating music note: "its part of her".
//
// WHAT IT IS. `public/models/lyraei.glb` — REBUILT IN BLENDER 2026-09-22
// (master gamemaster/models/sculpt/glb/lyraei.glb, builder
// gamemaster/models/sculpt/builders/lyraei_blender/, V'ael's pipeline), off
// Assets/terror-assets/Lyraea/lyraei_idle_1 (+ _left_1, _back). The 2026-09-16
// SDF body is kept beside it as lyraei_v1_sdf.glb. Metres, feet at y=0,
// facing +z, crown at 1.618 (MODEL_TOP). 18-bone skeleton (V'ael's layout,
// no tail) — the same names this file has always posed. 13 meshes:
// skin (face texture), hair (+plait), robe, underdress, underskirt, hood,
// sash, tabard (runes off the art), lining, cuffs, boots, soles, note.
// v2 (2026-09-23, the love-and-attention pass): one sculpted hair mass + a
// real plait, the face repainted, the cowl, and sleeves built FOR this
// file's idle (the bell's flap rides the upper arm so it hangs when the
// hands fold). `cuffs` = the black band on the sleeve openings — NO_INK
// already names it, so no change here beyond MODEL_TOP.
// v3 (2026-09-25, the head pass): the cranium squashed 21 % above the
// brows to the art (the hair crown 1.649 → 1.618), the hairline arch,
// the plait re-seated, the face-framing locks, the hood's back roll 3 cm
// lower, the face repainted. Same 13 mesh names, same 18 joints — only
// MODEL_TOP changed here (so at the same h her body draws ~1.9 % larger).
// Idle: hands folded at the waist, a slow sway to her own tune.
// Walk: driven from ctl.speed, eased in and out.
//
// Same contract and safety net as vaelModel3D.jsx: <LyraeiSculpted
// ctl h Fallback> for r3f scenes; the painted LyraeiBody stands in while
// the file loads or if it fails. PERF LAW: no React state from a frame.
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

export const LYRAEI_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/lyraei.glb`;
const MODEL_TOP = 1.618;     // measured at export (Blender build v3, 2026-09-25: 1.6184; v2 1.6489): hair crown incl.
const NO_INK = /note|sash|cuffs|lining/;   // lining = the black inside of the bell sleeves (Blender build)
const SELF_LIGHT = 0.4;
const GLOW = 0xb45cff;               // the drawing's violet outline, as a soft hull

let RAMP = null;
const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([172, 172, 172, 255, 228, 228, 228, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter;
  RAMP.needsUpdate = true;
  return RAMP;
};
const hullMat = (color, push, opacity) => {
  const m = new THREE.MeshBasicMaterial({ color, side: THREE.BackSide, transparent: opacity < 1, opacity, depthWrite: opacity >= 1 });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace("#include <begin_vertex>", `#include <begin_vertex>\n  transformed += normalize( normal ) * ${push.toFixed(4)};`);
  };
  return m;
};
let INK = null, HALO = null;
const inkMat = () => (INK = INK || hullMat(0x161114, 0.0032, 1));
const haloMat = () => (HALO = HALO || hullMat(GLOW, 0.009, 0.28));

function prepare(scene) {
  const root = cloneSkinned(scene);
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  let note = null;
  meshes.forEach((o) => {
    const m = o.material;
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    o.material = new THREE.MeshToonMaterial({
      color: m.color, map: m.map, gradientMap: toonRamp(), side: m.side,
      emissive: m.color.clone().multiplyScalar(SELF_LIGHT), emissiveMap: m.map || null,
    });
    o.frustumCulled = false;
    if (o.name === "lyraei_note") {
      note = o;
      o.material.emissive = new THREE.Color(0x9a5ce0);
      o.userData.p0 = o.position.clone();
      return;
    }
    if (NO_INK.test(o.name)) return;
    for (const [mat, tag] of [[inkMat(), "_ink"], [haloMat(), "_halo"]]) {
      if (tag === "_halo" && !/hair|robe|sleeves|braid/.test(o.name)) continue;   // the glow hugs the silhouette only
      const h = o.isSkinnedMesh ? new THREE.SkinnedMesh(o.geometry, mat) : new THREE.Mesh(o.geometry, mat);
      if (o.isSkinnedMesh) h.bind(o.skeleton, o.bindMatrix);
      h.name = o.name + tag; h.frustumCulled = false;
      o.parent.add(h);
    }
  });
  const B = {};
  root.traverse((o) => { if (o.isBone) B[o.name] = o; });
  if (B.hips) B.hips.userData.y0 = B.hips.position.y;
  return { root, B, note };
}

function lyraeiPose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const sw = Math.sin(ph), still = 1 - amt;
  for (const [k, s, off] of [["L", 1, 0], ["R", -1, Math.PI]]) {
    const lp = ph + off, fwd = Math.sin(lp), swing = Math.max(0, Math.cos(lp));
    set("thigh" + k, (-fwd * 0.26 - swing * 0.1) * amt, 0, 0);
    set("shin" + k, swing * 0.45 * amt, 0, 0);
    set("foot" + k, (-swing * 0.25 + Math.max(0, -fwd) * 0.12) * amt, 0, 0);
    set("upperarm" + k, -0.42 * still + fwd * 0.16 * amt, 0, -s * 0.2 * still + s * 0.04 * amt);
    set("forearm" + k, -1.05 * still - 0.15 * amt, 0, -s * 0.55 * still);
    set("hand" + k, 0.1 * still, 0, -s * 0.25 * still);
  }
  set("hips", 0, sw * 0.05 * amt, Math.sin(t * 0.5) * 0.015 * still);
  if (B.hips) B.hips.position.y = B.hips.userData.y0 + (Math.abs(Math.cos(ph)) - 0.6) * 0.016 * amt;
  set("spine", 0, -sw * 0.04 * amt, 0);
  set("chest", Math.sin(t * 1.4) * 0.012, -sw * 0.04 * amt, 0);
  set("head", 0.02 + Math.sin(t * 0.7) * 0.02, Math.sin(t * 0.35) * 0.2 * (1 - 0.6 * amt), Math.sin(t * 0.9) * 0.05);
}

export function LyraeiModel({ ctl, h = 1.3 }) {
  const gltf = useLoader(GLTFLoader, LYRAEI_GLB_URL);
  const { root, B, note } = useMemo(() => prepare(gltf.scene), [gltf]);
  const g = useRef(); const yaw = useRef(0); const ph = useRef(0); const amt = useRef(0);
  useFrame(({ clock }, dtRaw) => {
    const o = g.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 0.7;
    const f = (ctl && ctl.facing) || { x: 0, y: 1 };
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 9);
    o.rotation.y = yaw.current;
    const speed = (ctl && ctl.speed) || 0;
    amt.current += ((speed ? 1 : 0) - amt.current) * Math.min(1, dt * 6);
    ph.current += (speed ? 1 : amt.current) * dt * 6.5;
    lyraeiPose(B, t, ph.current, amt.current);
    if (note) {   // the note drifts and bobs beside her head
      const p0 = note.userData.p0;
      note.position.set(p0.x + Math.sin(t * 0.8) * 0.012, p0.y + Math.sin(t * 1.6) * 0.02, p0.z);
      note.rotation.z = Math.sin(t * 1.6 + 0.8) * 0.18;
    }
  });
  return (
    <group ref={g}>
      <primitive object={root} scale={h / MODEL_TOP} />
    </group>
  );
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[lyraei] lyraei.glb did not load — the painted body stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

export function LyraeiSculpted({ ctl, h, Fallback }) {
  const fb = Fallback ? <Fallback ctl={ctl} h={h} /> : null;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <LyraeiModel ctl={ctl} h={h} />
      </Suspense>
    </GlbBoundary>
  );
}

export default LyraeiModel;
